import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { inputStyle } from '../lib/ui.js';
import { DAYS, DAY_LABELS, leadDays, unansweredDays, fmtDay, describeRow } from '../lib/weekly-grid.js';

// The staff availability editor (docs/AVAILABILITY-WEEKLY-GRID-SPEC.md).
//
// One question per weekday — can't work, any time, or these hours — in place of
// the old form, which made staff choose "can't" vs "CAN", a recurrence and
// dates, and read a day they left blank as "available all day". Every day must
// be answered before saving. Managers still see and edit individual rows in
// AvailabilitySection.

const MODES = [
  { id: 'off', label: "Can't work" },
  { id: 'any', label: 'Any time' },
  { id: 'hours', label: 'These hours' },
];

const smallLink = {
  fontSize: 11, color: 'var(--forest)', background: 'transparent', border: 'none',
  cursor: 'pointer', padding: 0, fontFamily: 'inherit', textAlign: 'left',
};

// Top-level on purpose: a component declared inside another component's render
// remounts on every keystroke and the time inputs lose focus.
function DayRow({ day, answer, onChange }) {
  const mode = answer ? answer.mode : null;
  const windows = answer ? answer.windows : [];
  const choose = (m) => onChange(m === 'hours'
    ? { mode: 'hours', windows: windows.length ? windows : [{ start: '09:00', end: '17:00' }] }
    : { mode: m, windows: [] });
  const setWindow = (i, patch) => onChange({ mode: 'hours', windows: windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: 36, paddingTop: 6, fontWeight: 700, fontSize: 13, color: mode ? '#212121' : '#c2410c' }}>{DAY_LABELS[day]}</div>
      <div role="radiogroup" aria-label={`${DAY_LABELS[day]}`} style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {MODES.map((m) => (
          <button key={m.id} type="button" role="radio" aria-checked={mode === m.id} onClick={() => choose(m.id)}
            style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                     border: `1.5px solid ${mode === m.id ? 'var(--forest)' : '#d0d4d9'}`,
                     background: mode === m.id ? 'var(--forest)' : '#fff', color: mode === m.id ? '#fff' : '#37474f' }}>
            {m.label}
          </button>
        ))}
        {!mode && <span style={{ fontSize: 11, color: '#c2410c', alignSelf: 'center' }}>Not set</span>}
      </div>
      {mode === 'hours' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {windows.map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input type="time" step="1800" value={w.start} aria-label={`${DAY_LABELS[day]} from`}
                onChange={(e) => setWindow(i, { start: e.target.value })} style={{ ...inputStyle, width: 112 }} />
              <span style={{ color: '#757575' }}>–</span>
              <input type="time" step="1800" value={w.end} aria-label={`${DAY_LABELS[day]} until`}
                onChange={(e) => setWindow(i, { end: e.target.value })} style={{ ...inputStyle, width: 112 }} />
              {windows.length > 1 && (
                <button type="button" aria-label="Remove this time window" style={{ ...smallLink, color: '#c62828', fontSize: 13 }}
                  onClick={() => onChange({ mode: 'hours', windows: windows.filter((_, j) => j !== i) })}>✕</button>
              )}
            </div>
          ))}
          <button type="button" style={smallLink}
            onClick={() => onChange({ mode: 'hours', windows: [...windows, { start: '14:00', end: '18:00' }] })}>
            + another time window
          </button>
        </div>
      )}
    </div>
  );
}

export function WeeklyAvailabilityEditor({ currentUser, targetUserId }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState(null);
  const [days, setDays] = useState({});
  const [startsOn, setStartsOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  async function load() {
    try {
      const r = await api('GET', `/api/availability/weekly/${targetUserId}`, null, headers);
      setData(r);
      setDays(r.pattern || {});
      setStartsOn(r.starts_on);
    } catch (e) { setError(e.message || 'Failed to load'); }
  }
  useEffect(() => { load(); }, [targetUserId]);

  if (!data) return <div style={{ fontSize: 12, color: error ? '#c62828' : '#757575' }}>{error || 'Loading…'}</div>;

  const missing = unansweredDays(days);
  const needsApproval = startsOn && leadDays(data.today, startsOn) < data.ack_lead_days;

  async function save() {
    setSaving(true); setError(''); setSaved('');
    try {
      const r = await api('POST', '/api/availability/weekly', { employee_id: targetUserId, starts_on: startsOn, days }, headers);
      setSaved(r.status === 'active' ? 'Saved.'
        : r.approval_required ? 'Saved. Your manager needs to approve it.'
        : 'Saved. Your manager will see it.');
      await load();
    } catch (e) {
      let msg = e.message || 'Save failed';
      try { const parsed = JSON.parse(msg); if (parsed.details) msg = parsed.details.join(' · '); } catch { /* plain message */ }
      setError(msg);
    } finally { setSaving(false); }
  }

  async function removeOneOff(o) {
    if (!confirm(`Delete "${describeRow({ ...o, recurrence: 'one_time' })}"?`)) return;
    try {
      await api('DELETE', `/api/availability/reductions/${o.id}`, null, headers);
      await load();
    } catch (e) { setError(e.message || 'Delete failed'); }
  }

  const canSave = !saving && missing.length === 0 && !!startsOn;
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#212121', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Your weekly availability
      </div>
      <div style={{ fontSize: 11, color: '#757575', marginBottom: 6, lineHeight: 1.45 }}>
        For each day, pick whether you can work. Your answers repeat every week until you change them.
      </div>
      {data.pending && (
        <div style={{ fontSize: 12, padding: '6px 10px', background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: 6, marginBottom: 6 }}>
          Your availability starting {fmtDay(data.pending.starts_on)} is waiting for your manager
          {data.pending.status === 'pending_approval' ? ' to approve it' : ''}. Saving again replaces it.
        </div>
      )}
      {DAYS.map((d) => (
        <DayRow key={d} day={d} answer={days[d]} onChange={(a) => setDays((prev) => ({ ...prev, [d]: a }))} />
      ))}
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#37474f', marginTop: 10 }}>
        Starting
        <input type="date" value={startsOn} min={data.today} onChange={(e) => setStartsOn(e.target.value)}
          style={{ ...inputStyle, width: 160 }} />
      </label>
      {startsOn && (
        <div style={{ fontSize: 12, marginTop: 6, color: needsApproval ? '#92400e' : '#166534' }}>
          {needsApproval
            ? `${fmtDay(startsOn)} is less than 2 weeks away, so your manager will need to approve it.`
            : `${fmtDay(startsOn)} is 2+ weeks away, so your manager just needs to see it.`}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#c62828', marginTop: 6 }}>{error}</div>}
      {saved && <div style={{ fontSize: 12, color: '#166534', marginTop: 6 }}>{saved}</div>}
      <button type="button" onClick={save} disabled={!canSave}
        style={{ marginTop: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', fontFamily: 'inherit',
                 background: canSave ? 'var(--forest)' : '#cfd8dc', color: '#fff', cursor: canSave ? 'pointer' : 'default' }}>
        {saving ? 'Saving…' : missing.length > 0 ? `Answer every day to save (${missing.length} left)` : 'Save my availability'}
      </button>
      <div style={{ fontSize: 11, color: '#757575', marginTop: 8 }}>Need a specific day off? Use Time Off below.</div>
      {data.one_offs.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#37474f', marginBottom: 4 }}>One-off entries</div>
          {data.one_offs.map((o) => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0' }}>
              <span style={{ flex: 1 }}>{describeRow({ ...o, recurrence: 'one_time' })}</span>
              <button type="button" onClick={() => removeOneOff(o)}
                style={{ fontSize: 11, padding: '3px 8px', background: '#fff', color: '#c62828', border: '1px solid #ffcdd2', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
